import { createFileRoute, notFound, redirect, useRouter } from "@tanstack/react-router";
import { ErrorState, FullPageLoading } from "../components/states.tsx";
import type { ActionClientError } from "../lib/actions.ts";
import { staffActions } from "../lib/staff-actions.ts";
import { type StaffSessionState, staffSessionQuery } from "../lib/staff-session.ts";
import { StaffDoor } from "../screens/staff/StaffDoor.tsx";
import { StaffShell } from "../screens/staff/StaffShell.tsx";

/**
 * Everything under `/sysadmin`: Adea's own people, every role through this one door. Staff sign in here with their
 * own staff account (`/api/staff`), never the product's sign-in: signed out, or signed in by a mailed link without a
 * passkey yet, the page is the staff door. Operators see the run-log pages (`/sysadmin/runs`); sysadmins see
 * everything. The layout holds the shell, the confirmation dialog (restoring a company asks for its name) and the
 * toasts. Staff pages have no live channel: they refetch when you act.
 */
export const Route = createFileRoute("/_staff")({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(staffSessionQuery);
    if (!session.signedIn && session.notHere) throw notFound();
    if (!session.signedIn || !session.passkeyDone) throw new StaffDoorNeeded(session);
    // Operators have the run-log pages; every other staff page is a sysadmin's.
    if (session.staff.role === "operator" && !/^\/sysadmin\/runs(\/|$)/.test(location.pathname))
      throw redirect({ to: "/sysadmin/runs" });
    try {
      await context.queryClient.ensureQueryData(staffActions.queryOptions("staff.me", {}));
    } catch (error) {
      const code = (error as ActionClientError | null)?.code;
      if (code === "unauthenticated") {
        // The session ended between the two reads (12 hours, or turned off): the door again.
        context.queryClient.removeQueries({ queryKey: staffSessionQuery.queryKey });
        throw new StaffDoorNeeded({ signedIn: false });
      }
      if (code === "not_found" || code === "forbidden" || code === "unknown_action") throw notFound();
      throw error;
    }
  },
  pendingComponent: FullPageLoading,
  errorComponent: StaffError,
  component: StaffShell,
});

/** Not signed in as staff, or signed in by a mailed link without a passkey yet: the page is the staff door. */
class StaffDoorNeeded extends Error {
  constructor(readonly session: StaffSessionState) {
    super("the staff door");
    this.name = "StaffDoorNeeded";
  }
}

function StaffError({ error }: { error: unknown }) {
  const router = useRouter();
  if (error instanceof StaffDoorNeeded) return <StaffDoor state={error.session} />;
  return <ErrorState error={error} onRetry={() => void router.invalidate()} />;
}
